import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs';

import {
  KeyValueElevatedRoleModel,
  KeyValueIsDisabledModel,
  KeyValueIsUsedModel,
  KeyValueModel,
  KeyValueType,
} from '@models/common';
import { ListResponse } from '@models/helpers';
import { Permission } from '@models/permission/permissions';
import { Currency } from '@models/system';
import { PrimaryUser } from '@module-instance/assignments/primary-users-dialog/primary-user-dialog.model';
import { DataSourceInfoModel } from '@shared/components/fields/common';
import { HttpService } from '@shared/services';
import { DictionaryParams } from '@signal-store/models';

@Injectable({
  providedIn: 'root',
})
export class DictionaryService {
  private http = inject(HttpService);

  getDataSource(params: Pick<DataSourceInfoModel, 'id' | 'contractId' | 'moduleId'>) {
    const { moduleId, ...rest } = params;
    const headers = moduleId != null ? { moduleId: moduleId.toString() } : {};
    return this.http
      .post<ListResponse<KeyValueType>>('_api/FieldDefinition/SearchDataSourceList', rest, {
        httpOptions: { headers },
        useLoader: false,
      })
      .pipe(map(({ data }) => data));
  }

  getSearchEntityInstances(
    params: Pick<
      DictionaryParams,
      'contractId' | 'relationId' | 'entityInstanceId' | 'moduleId' | 'fieldId' | 'selectedIds'
    >,
  ) {
    const { moduleId, ...rest } = params;
    const headers = moduleId != null ? { moduleId: moduleId.toString() } : {};
    return this.http
      .post<ListResponse<KeyValueIsUsedModel<string, string>>>('_api/FieldDefinition/SearchEntityInstances', rest, {
        httpOptions: { headers },
        useLoader: false,
      })
      .pipe(map(({ data }) => data.map(({ key, ...rest }) => ({ ...rest, key: key.toString() }))));
  }

  getRoles() {
    return this.http.get<KeyValueElevatedRoleModel<number, string>[]>(`_api/Role/Dict`, { useLoader: false });
  }

  getPrimaryUsers(contractId: number) {
    const params = this.http.buildHttpParams({ contractId });
    return this.http.get<PrimaryUser[]>(`_api/Role/GetContractPrimary`, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getCountryCodes() {
    return this.http.get<KeyValueType<string, string>[]>(`_api/Main/CountryCodes`, { useLoader: false });
  }

  getIndustries() {
    return this.http.get<KeyValueType<number, string>[]>(`_api/Main/Industries`, { useLoader: false });
  }

  getIntervalDict() {
    return this.http.get<KeyValueType<number, string>[]>(`_api/FinancialSchedule/IntervalDict`, { useLoader: false });
  }

  getCurrencies() {
    return this.http.get<Currency[]>(`_api/Currency/Dict`, { useLoader: false });
  }

  getModuleRoles(params: Pick<DictionaryParams, 'moduleId' | 'contractId'>) {
    const httpParams = this.http.buildHttpParams(params);
    return this.http.get<KeyValueType<number>[]>('_api/ModuleInstance/ModuleRolesDict', {
      httpOptions: { params: httpParams },
      useLoader: false,
    });
  }

  getModuleUserRoles(params: Pick<DictionaryParams, 'moduleId' | 'contractId'>) {
    const { moduleId, contractId } = params;
    const httpParams = this.http.buildHttpParams({
      id: moduleId,
      permissionId: Permission.Create,
      ...(contractId ? { contractId } : {}),
    });
    return this.http.get<KeyValueType<number>[]>('_api/ModuleDefinition/ModuleRoleUserDict', {
      httpOptions: { params: httpParams },
      useLoader: false,
    });
  }

  taggingUsersDict(params: Pick<DictionaryParams, 'contractId' | 'moduleId'>) {
    const { contractId, moduleId } = params;
    const httpParams = this.http.buildHttpParams({ contractId });
    return this.http.get<KeyValueType[]>('_api/ModuleInstance/TaggingUsersDict', {
      httpOptions: { params: httpParams, headers: { moduleId: moduleId?.toString() } },
      useLoader: false,
    });
  }

  getOrganizations(contractId?: number) {
    const params = contractId ? this.http.buildHttpParams({ contractId }) : undefined;
    return this.http.get<KeyValueType<number, string>[]>(`_api/Organization/Dict`, {
      useLoader: false,
      httpOptions: { params },
    });
  }

  getUsersDict() {
    return this.http.get<KeyValueIsDisabledModel<number, string>[]>(`_api/User/Dict`, { useLoader: false });
  }

  getMimeTypesDict() {
    return this.http.get<KeyValueModel<number, string>[]>(`_api/FieldDefinition/MimeTypesDict`, { useLoader: false });
  }

  getContractsDict(userId: number) {
    const params = this.http.buildHttpParams({ userId });
    return this.http.get<KeyValueType<number, string>[]>(`_api/Role/ContractsDict`, {
      useLoader: false,
      httpOptions: { params },
    });
  }
}

export const makeKey = (params: Partial<DictionaryParams>) => {
  return JSON.stringify(
    Object.keys(params)
      .sort()
      .reduce(
        (acc, key) => {
          acc[key] = params[key];
          return acc;
        },
        {} as Record<string, any>,
      ),
  );
};
